import { StandardEvents } from '@shopify/events';
import '@theme/product-form';

let offerInstance = 0;

/** UI only: the native product form owns mutations, errors, and cart section events. */
class HvCartOffer extends HTMLElement {
  #controller;
  #request;
  #observer;
  #opener;
  #pending = false;
  #loaded = false;
  #error = '';
  #instance = ++offerInstance;
  #stateRequest;
  #updateEpoch = 0;
  #variantEpoch = 0;
  #variantPending;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#click, { signal });
    this.addEventListener('change', this.#select, { signal });
    this.addEventListener('keydown', this.#key, { signal });
    this.addEventListener('submit', this.#guardSubmit, { capture: true, signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#cartUpdate, { signal });
    document.addEventListener(StandardEvents.productSelect,this.#variantUpdate,{signal});
    this.#estimateTiers();
    this.addEventListener(StandardEvents.cartError, this.#cartError, { signal });
    this.#observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !this.#loaded) this.#load();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#request?.abort();
    this.#observer?.disconnect();
    this.#stateRequest?.abort();
  }

  async #load() {
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.#loaded = true;
    try {
      const url = new URL(this.dataset.sourceUrl, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid candidate URL');
      const response = await fetch(url, { signal: request.signal, cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) throw new Error('Candidates unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (request.signal.aborted || !this.isConnected || this.#request !== request) return;
      const content = html.querySelector('[data-offer-response]');
      const target = this.querySelector('[data-offer-candidates]');
      if (!content || !target) throw new Error('Candidates unavailable');
      if (this.dataset.excludeProduct) content.querySelectorAll('[data-offer-product]').forEach(card => {
        if (card.dataset.offerProduct === this.dataset.excludeProduct) card.remove();
      });
      target.replaceChildren(content);
      this.querySelector('[data-offer-retry]').hidden = true;
      this.querySelector('[data-offer-status]').textContent = '';
      const rail = this.querySelector('[data-offer-rail]');
      const slider = this.querySelector('[data-offer-slider]');
      if (rail && slider) {
        const update = () => {
          const overflow = rail.scrollWidth - rail.clientWidth;
          slider.hidden = overflow <= 1;
          slider.value = overflow > 0 ? String(Math.round(Math.abs(rail.scrollLeft) / overflow * 100)) : '0';
        };
        rail.addEventListener('scroll', update, { passive: true, signal: request.signal });
        slider.addEventListener('input', () => {
          const overflow = rail.scrollWidth - rail.clientWidth;
          rail.scrollLeft = overflow * Number(slider.value) / 100 * (getComputedStyle(rail).direction === 'rtl' ? -1 : 1);
        }, { signal: request.signal });
        const resize = new ResizeObserver(update);
        resize.observe(rail);
        request.signal.addEventListener('abort', () => resize.disconnect(), { once: true });
        requestAnimationFrame(update);
      }
      const hasCards = !!this.querySelector('[data-offer-product]');
      this.querySelector('.hv-cart-offer-prompt')?.toggleAttribute('hidden', !hasCards);
      target.toggleAttribute('hidden', !hasCards);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#loaded = false;
      this.querySelector('[data-offer-retry]').hidden = false;
      this.querySelector('[data-offer-status]').textContent = this.dataset.labelUnavailable;
    }
  }

  #click = (event) => {
    if (event.target.closest('[data-offer-retry]')) { this.#load(); return; }
    const choose = event.target.closest('[data-offer-choose]');
    if (choose) {
      if (this.#pending) return;
      this.#close(false);
      this.#opener = choose;
      const template = choose.closest('[data-offer-product]')?.querySelector('template');
      const panel = this.querySelector('[data-offer-panel]');
      if (!template || !panel) return;
      const fragment = template.content.cloneNode(true);
      const ids = new Map();
      fragment.querySelectorAll('[id]').forEach(node => { const old = node.id; node.id = `${old}-offer-${this.#instance}`; ids.set(old,node.id); });
      fragment.querySelectorAll('*').forEach(node => {
        for (const attr of ['for','form','aria-controls','aria-describedby','aria-labelledby']) {
          if (node.hasAttribute(attr)) node.setAttribute(attr,node.getAttribute(attr).split(' ').map(id => ids.get(id) || id).join(' '));
        }
      });
      panel.replaceChildren(fragment);
      panel.hidden = false;
      choose.setAttribute('aria-expanded', 'true');
      this.#error = '';
      this.querySelector('[data-offer-status]').textContent = '';
      panel.querySelector('input[type="radio"]:not(:disabled)')?.focus();
    } else if (event.target.closest('[data-offer-close]')) {
      this.#close();
    }
  };

  #close(restore = true) {
    if (this.#pending) return;
    const panel = this.querySelector('[data-offer-panel]');
    if (panel) { panel.hidden = true; panel.replaceChildren(); }
    this.#estimateTiers();
    this.#opener?.setAttribute('aria-expanded', 'false');
    if (restore && this.#opener?.isConnected) this.#opener.focus();
  }

  #key = (event) => {
    if (event.key === 'Escape' && event.target.closest('[data-offer-panel]') && !this.#pending) {
      event.preventDefault();
      event.stopPropagation();
      this.#close();
    }
  };

  #money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: this.dataset.currency,
    }).format(cents / 100);
  }

  #select = (event) => {
    const radio = event.target.closest('input[data-offer-price]');
    if (!radio || radio.disabled || this.#pending) return;
    const form = radio.closest('form');
    form.querySelector('input[name="id"]').value = radio.value;
    form.querySelector('[ref="addToCartButton"]').disabled = false;
    const price = Number(radio.dataset.offerPrice);
    form.querySelector('[data-offer-selection-price]').textContent = this.#money(price);
    this.#estimateTiers(price, this.querySelector('[data-offer-panel] .hv-cart-offer-panel__heading strong')?.textContent + ' · ' + radio.closest('label').textContent.trim());
    const estimate = form.querySelector('[data-offer-estimate]');
    estimate.hidden = true;
  };

  #estimateTiers(price = Number(this.dataset.basisPrice), title = this.dataset.basisTitle) {
    const count = Number(this.dataset.eligibleCount);
    const basis = this.querySelector('[data-offer-basis]');
    if (basis) basis.textContent = title && price > 0 ? this.dataset.labelBasis.replace('__title__', title) : '';
    this.closest('.hv-cart-benefit--bundle')?.querySelectorAll('[data-offer-tier]').forEach(tier => {
      const estimate = tier.querySelector('[data-offer-tier-estimate]');
      const rate = Number(tier.dataset.offerTier);
      const target = rate === 10 ? 2 : rate === 15 ? 3 : 4;
      const remaining = Math.max(0,target - count);
      const show = price > 0 && !tier.classList.contains('is-current');
      estimate.hidden = !show;
      estimate.replaceChildren();
      if (show) {
        const [before, after = ''] = this.dataset.labelEstimate.split('__amount__');
        const amount = document.createElement('span');
        amount.className = 'hv-cart-offer-estimate-amount';
        amount.textContent = this.#money(Math.round((Number(this.dataset.eligibleBase) + remaining * price) * rate / 100));
        estimate.append(before, amount, after);
      }
    });
  }

  #variantUpdate = (event) => {
    if (this.dataset.offerSurface !== 'pdp' || !(event.target instanceof Element) || event.target.closest('product-card,quick-add-dialog,hv-cart-offer')) return;
    const epoch = ++this.#variantEpoch;
    this.#variantPending = event.promise;
    event.promise?.then(({detail}) => {
      if (!this.isConnected || epoch !== this.#variantEpoch) return;
      const variant = detail?.resource;
      this.dataset.basisPrice = variant?.available ? String(variant.price) : '0';
      const name = this.dataset.basisTitle.split(' · ')[0];
      this.dataset.basisTitle = `${name} · ${variant?.title || ''}`;
      this.#close(false);
    }).catch(() => {});
  };

  #guardSubmit = (event) => {
    if (!event.target.closest('product-form-component')) return;
    if (this.#pending || !event.target.querySelector('input[name="id"]')?.value) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  #cartError = (event) => {
    this.#error = typeof event.detail?.description === 'string' ? event.detail.description
      : typeof event.error === 'string' ? event.error : this.dataset.labelAddError;
  };

  async #refreshPdp(epoch) {
    const pendingVariant = this.#variantPending;
    await pendingVariant?.catch(() => {});
    if (pendingVariant !== this.#variantPending) return this.#refreshPdp(epoch);
    if (!this.isConnected || epoch !== this.#updateEpoch) return null;
    const variantEpoch = this.#variantEpoch;
    const wrapper = this.closest('[data-hv-pdp-offer-state]');
    if (!wrapper || !this.dataset.stateUrl) return this;
    this.#stateRequest?.abort();
    const request = new AbortController();
    this.#stateRequest = request;
    const url = new URL(this.dataset.stateUrl,location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid offer state URL');
    if (url.pathname === location.pathname && new URL(location.href).searchParams.has('variant')) url.searchParams.set('variant',new URL(location.href).searchParams.get('variant'));
    const response = await fetch(url,{signal:request.signal,cache:'no-store',credentials:'same-origin'});
    if (!response.ok) throw new Error('Offer update unavailable');
    const html = new DOMParser().parseFromString(await response.text(),'text/html');
    const replacement = html.querySelector('[data-hv-pdp-offer-state]');
    if (!replacement || request.signal.aborted || !this.isConnected || epoch !== this.#updateEpoch) return null;
    if (variantEpoch !== this.#variantEpoch) return this.#refreshPdp(epoch);
    const nextOffer = replacement.querySelector('hv-cart-offer');
    if (nextOffer) {
      nextOffer.dataset.basisPrice = this.dataset.basisPrice;
      nextOffer.dataset.basisTitle = this.dataset.basisTitle;
    }
    wrapper.replaceWith(replacement);
    return replacement.querySelector('hv-cart-offer');
  }

  #cartUpdate = (event) => {
    const epoch = ++this.#updateEpoch;
    const own = this.contains(event.target);
    if (own) {
      this.#pending = true;
      this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('[data-offer-panel] button,[data-offer-panel] input[type="radio"]').forEach((e) => { e.disabled = true; });
    }
    event.promise?.then(async ({ detail }) => {
      // Native cart section morph subscribers run first. Do not replace their markup or amounts.
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (epoch !== this.#updateEpoch) return;
      const surface = this.dataset.offerSurface || 'cart';
      const current = surface === 'pdp'
        ? await this.#refreshPdp(epoch)
        : this.isConnected ? this : document.querySelector('hv-cart-offer[data-offer-surface="cart"]');
      if (!(current instanceof HvCartOffer)) return;
      current.#pending = false;
      current.removeAttribute('aria-busy');
      current.#close(false);
      const currentEpoch = current.#updateEpoch;
      await current.#load();
      if (!current.isConnected || current.#updateEpoch !== currentEpoch) return;
      if (own) {
        current.querySelector('[data-offer-status]').textContent = detail?.didError
          ? this.#error || current.dataset.labelAddErrorSize
          : current.dataset.labelAdded;
        const focus = current.querySelector('[data-offer-choose]') || document.querySelector('#cart-drawer-heading');
        const openDrawer = document.querySelector('cart-drawer-component dialog[open]');
        if (focus instanceof HTMLElement && (!openDrawer || openDrawer.contains(current))) {
          if (!focus.matches('button,a,input,[tabindex]')) focus.setAttribute('tabindex', '-1');
          focus.focus({ preventScroll: true });
        }
      }
    }).catch(() => {
      this.#pending = false;
      this.removeAttribute('aria-busy');
      if (!this.isConnected) return;
      this.querySelectorAll('[data-offer-panel] input[type="radio"]').forEach((e) => { e.disabled = e.closest('label').classList.contains('is-unavailable'); });
      const add = this.querySelector('[data-offer-panel] [ref="addToCartButton"]');
      if (add) add.disabled = !this.querySelector('[data-offer-panel] input:checked');
      const close = this.querySelector('[data-offer-close]');
      if (close) close.disabled = false;
      this.querySelector('[data-offer-status]').textContent = this.dataset.labelAddError;
    });
  };
}
if (!customElements.get('hv-cart-offer')) customElements.define('hv-cart-offer', HvCartOffer);
